import { QueryClient, queryOptions } from "@tanstack/react-query";

import { supabase } from "@/lib/supabase";
import { Tables } from "@/types/supabase-types";
import { SupabaseRealtimeChannel } from "@/lib/supabase/supabase-realtime-channel";
import { REALTIME_POSTGRES_CHANGES_LISTEN_EVENT, RealtimePostgresUpdatePayload } from "@supabase/supabase-js";

export type SyncMetadata = Tables<"SyncMetadata">;

const syncMetadataQuery = supabase
  .from("SyncMetadata")
  .select("*")
  .order("lastUpdated", { ascending: true })
  .throwOnError();

export const syncMetadataQueryOptions = queryOptions({
  queryKey: ["SyncMetadata", "all"],
  queryFn: async () => (await syncMetadataQuery).data,
  select: (data) => data.map(({ lastUpdated, ...record }) => ({ ...record, lastUpdated: new Date(lastUpdated) })),
});

/**
 * Subscribes to changes in the SyncMetadata table and updates the provided query client with the new data. using the
 * ["SyncMetadata", "all"] query key.
 *
 * Note that any errors that occur in the channel will be logged to the console, but will not throw an error.
 *
 * @param queryClient - The QueryClient instance to update with the new data.
 * @returns A function that can be used to asynchronously dispose (unsubscribe) the channel.
 */
export const subscribeToSyncMetadataChanges = (queryClient: QueryClient): (() => Promise<void>) => {
  // Create a new realtime channel for listening to changes in the SyncMetadata table
  // NOTE: Any errors that occur in the channel will be logged to the console, but will not throw an error
  const channel = new SupabaseRealtimeChannel(supabase, "SyncMetadataChanges", (error) => {
    console.warn("An error occured in the SyncMetadataChanges realtime channel:", error);
  }).onPostgresChange(
    { event: REALTIME_POSTGRES_CHANGES_LISTEN_EVENT.UPDATE, schema: "public", table: "SyncMetadata" },
    (newRecord: RealtimePostgresUpdatePayload<Tables<"SyncMetadata">>) => {
      queryClient.setQueryData(["SyncMetadata", "all"], (oldData: SyncMetadata[]) => {
        const newData = structuredClone(oldData);
        const oldRecordIndex = newData.findIndex((x) => x.entityType === newRecord.new.entityType);

        if (oldRecordIndex !== -1) {
          newData[oldRecordIndex] = newRecord.new;
        } else {
          newData.push(newRecord.new);
        }

        return newData.sort((a, b) => a.lastUpdated.localeCompare(b.lastUpdated));
      });
    },
  );

  // Initialize the channel subscription, do not wait for success
  void channel.subscribeAsync();

  // Return a function that can be used to dispose (unsubscribe) the channel
  return async () => {
    await channel[Symbol.asyncDispose]();
  };
};
